import {
  Button,
  Checkbox,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
} from "@material-ui/core";
import React, { ReactElement, useMemo } from "react";
import { makeStyles } from "@material-ui/styles";

import { theme } from "../../../theme";

export type FilterSelectMenuProps<T> = {
  emptyStateTooltip?: string;
  label: string;
  onSelectValue: (values: string[]) => void;
  onSelectAll: () => void;
  onClear: () => void;
  options: { label: string; value: T }[];
  selectedValues: string[];
  selectedValueAsDisplayString: (v: string) => string;
  optionValueAsHTMLAttrString: (o: T) => string;
  renderMenuItemLabel?: (o: { label: string; value: T }) => React.ReactNode;
};

const useStyles = makeStyles(() => ({
  menu: {
    width: 180,
    position: "relative" as "relative",
  },
  menuLabel: {
    display: "block",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  menuItem: {
    minWidth: 180,
  },
}));

type ConditionalTooltipProps = {
  isShowing: boolean;
  title: string;
  children: ReactElement<any, any>;
};

function ConditionalTooltip({
  isShowing,
  title,
  children,
}: ConditionalTooltipProps) {
  return isShowing ? (
    <Tooltip title={title} arrow>
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  );
}

function FilterSelectMenu<T>({
  emptyStateTooltip = "No options available to filter with.",
  label,
  onSelectValue,
  onSelectAll,
  onClear,
  options,
  selectedValues,
  selectedValueAsDisplayString,
  optionValueAsHTMLAttrString,
  renderMenuItemLabel = (o) => o.label,
}: FilterSelectMenuProps<T>) {
  const classes = useStyles();

  const labelText = useMemo(() => {
    if (selectedValues.length === 0) {
      return "All";
    }

    return `${selectedValues
      .map(selectedValueAsDisplayString)
      .sort()
      .join(", ")}`;
  }, [selectedValues, selectedValueAsDisplayString]);

  const isEmpty = options.length === 0;

  return (
    <ConditionalTooltip isShowing={isEmpty} title={emptyStateTooltip}>
      <TextField
        className={classes.menu}
        disabled={isEmpty}
        label={label}
        select
        size="small"
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          displayEmpty: true,
          multiple: true,
          MenuProps: {
            anchorOrigin: {
              horizontal: "left",
              vertical: "bottom",
            },
            anchorPosition: { top: 200, left: 0 },
            anchorReference: "anchorEl",
            getContentAnchorEl: null, // allows us to customize the offset position of the Popover
          },
          onChange: (e) => {
            const value = e.target.value as string[];
            onSelectValue(value);
          },
          renderValue: () => {
            return (
              <ConditionalTooltip isShowing={!isEmpty} title={labelText}>
                <span className={classes.menuLabel}>{labelText}</span>
              </ConditionalTooltip>
            );
          },
        }}
        variant="outlined"
        value={selectedValues}
      >
        <MenuItem
          style={{
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          }}
        >
          <Grid container spacing={1} alignContent="space-between">
            <Grid item xs={6}>
              <Button
                fullWidth
                color="primary"
                size="small"
                variant="text"
                onClick={(e) => {
                  e.persist();
                  e.preventDefault();
                  e.stopPropagation();
                  onSelectAll();
                }}
              >
                Select All
              </Button>
            </Grid>

            <Grid item xs={6}>
              <Button
                fullWidth
                color="inherit"
                size="small"
                variant="text"
                onClick={(e) => {
                  e.persist();
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </MenuItem>
        {options
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((o) => (
            <MenuItem
              className={classes.menuItem}
              key={o.label}
              value={optionValueAsHTMLAttrString(o.value)}
            >
              <Grid container spacing={0} alignContent="space-between">
                <Grid item xs={10}>
                  {renderMenuItemLabel(o)}
                </Grid>
                <Grid item xs={2} style={{ textAlign: "right" }}>
                  <Checkbox
                    checked={selectedValues.includes(
                      optionValueAsHTMLAttrString(o.value)
                    )}
                    style={{ padding: 0 }}
                  />
                </Grid>
              </Grid>
            </MenuItem>
          ))}
      </TextField>
    </ConditionalTooltip>
  );
}

export default FilterSelectMenu;
